import axios from "axios";
import { ENV } from "../configs/environment";
import { getParams } from "../utlis/helpers";
import store from "../state/store";
import { setAPILoader, setErrorDisplay } from "../state/actions/globalAction";
import { logoOut } from "./websiteApi";
import { getCurrentLocale, pageLocale } from "../utlis/locale";
import { postWebViewMessage } from "../utlis/webViewBridge";

// Public axios instance — no auth interceptors (used for unauthenticated endpoints)
const publicApi = axios.create({ baseURL: ENV.API_URL });

export const verifyRecipientEmail = async (token) => {
  const response = await publicApi.post("v5/report/recipientVerification", {
    token,
  });
  return response.data;
};

const { errorCode } = pageLocale("common");

// Create axios instance for V5 API
const v5Api = axios.create({
  baseURL: ENV.API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token and locale
v5Api.interceptors.request.use(
  (config) => {
    // Try to get token from helpers (URL params) first, then sessionStorage if needed
    // Assuming getParams() returns the current session params including accessToken
    const { accessToken } = getParams();

    // If accessToken is available in params/session, add it to headers
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    } else {
      // Fallback to sessionStorage if that's where it's stored for some flows
      // This part depends on how the app manages tokens. Based on websiteApi.js, it seems to use getParams()
      let storedToken = sessionStorage.getItem("access_token");
      if (!storedToken) {
        try {
          storedToken =
            JSON.parse(
              sessionStorage.getItem("mmtSession") ||
                localStorage.getItem("mmtSession") ||
                "null",
            )?.accessToken || "";
        } catch {
          storedToken = "";
        }
      }
      if (storedToken) {
        config.headers.Authorization = `Bearer ${storedToken}`;
      }
    }

    // Forward user's locale so backend can return localized content
    // Render-document requests may explicitly select their representation.
    // Preserve that selection instead of replacing it with the shell locale.
    if (!config.headers["Accept-Language"]) {
      config.headers["Accept-Language"] = getCurrentLocale();
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling (matching websiteApi.js pattern)
v5Api.interceptors.response.use(
  (response) => {
    // Check for custom status 0 if that's a pattern, though V5 docs say 200/201 for success
    if (response.data && response.data.status === 0) {
      return Promise.reject(new Error(response.data.message || "API Error"));
    }
    return response;
  },
  (error) => {
    const { accessToken, locale } = getParams();

    if (error.response?.status === 401 || error.response?.status === 403) {
      // Handle unauthorized access similar to websiteApi.js
      store.dispatch(setErrorDisplay(errorCode));
      store.dispatch(setAPILoader(true));
      logoOut({ globalSignOut: false, token: accessToken })
        .then(() => {
          sessionStorage.clear();
          localStorage.removeItem("mmtSession");
          localStorage.removeItem("lastVisitedUrl");
          // Token expired: notify the WebView shell (if any) and always ask
          // the user to log in again on the web login screen.
          postWebViewMessage("TOKEN_INVALID");
          window.location.href = `/login?locale=${locale || "en"}`;
          store.dispatch(setAPILoader(false));
        })
        .catch(() => {
          store.dispatch(setAPILoader(false));
        });
    } else {
      // For other errors, just return the error info but don't necessarily dispatch global error display
      // unless we want to force it? keeping it local for now to let caller handle or dispatch
      // Actually websiteApi.js dispatches error, let's keep consistency if needed,
      // but explicit error handling in components is often better.
      // For now, let's return the simplified error message
    }
    // For other errors, return the error info but let caller handle
    return Promise.reject(error);
  },
);

/**
 * Vitals Management
 */

// Create Vitals
export const createVitals = async (vitalsData, seniorId, options = {}) => {
  let url = "/v5/vitals";
  if (seniorId) {
    url += `?seniorId=${seniorId}`;
  }
  console.log("[v5Api] POST", url, {
    vitalsData,
    idempotencyKey: options.requestId || null,
  });
  try {
    // requestId is already included in the JSON payload and the API uses it
    // as its idempotency fallback. Avoid a custom Idempotency-Key header here:
    // the dev API's CORS allowlist does not currently permit that header.
    const response = await v5Api.post(url, vitalsData);
    console.log("[v5Api] POST", url, "ok", {
      status: response.status,
      data: response.data,
    });
    return response.data;
  } catch (error) {
    console.error("[v5Api] POST", url, "failed", {
      status: error?.response?.status,
      data: error?.response?.data,
      message: error?.message,
    });
    throw error;
  }
};

// List Vitals
export const listVitals = async (seniorId) => {
  let url = "/v5/vitals";
  if (seniorId) {
    url += `?seniorId=${seniorId}`;
  }
  const response = await v5Api.get(url);
  return response.data;
};

// Get Vitals by ID
export const getVitalsById = async (id) => {
  const response = await v5Api.get(`/v5/vitals/${id}`);
  return response.data;
};

/**
 * Risk Assessment
 */

// Get Questionnaire
export const getQuestionnaire = async (seniorId) => {
  const response = await v5Api.get(
    `/v5/risk-assessment/questionnaire?seniorId=${seniorId}`,
  );
  return response.data;
};

// Submit Questionnaire
export const submitQuestionnaire = async (responses, personId) => {
  const response = await v5Api.post(
    `/v5/risk-assessment/submit?seniorId=${personId}`,
    responses,
  );
  return response.data;
};

// List Risk Assessments
export const listRiskAssessments = async (personId) => {
  const response = await v5Api.get(
    `/v5/risk-assessment/list?seniorId=${personId}`,
  );
  return response.data;
};

// Get Risk Assessment by ID
export const getRiskAssessmentById = async (id, seniorId) => {
  const response = await v5Api.get(
    `/v5/risk-assessment/summary/${id}?seniorId=${seniorId}`,
  );
  return response.data;
};

// Get the latest Mobility Insights screening saved for a SeniorLife person.
export const getLatestMobilityInsight = async (seniorId) => {
  const response = await v5Api.get(
    `/v5/mobility-insights/${encodeURIComponent(seniorId)}`,
  );
  return response.data;
};

/**
 * Care Plan
 */

// Get Care Plan for a senior
export const getCarePlan = async (seniorUuid) => {
  const response = await v5Api.get(`/v5/seniors/${seniorUuid}/care-plan`);
  return response.data;
};

/**
 * Mobility Event Summary
 */
export const getLatestEventSummary = async (seniorUuid, moduleId) => {
  const response = await v5Api.get(
    `/v5/seniors/${seniorUuid}/events/latest/summary?moduleId=${moduleId}`,
  );
  return response.data;
};

/**
 * Aggregated recent report (v6). The backend resolves the latest report metrics
 * across the senior's measurement modules, so no moduleId is required.
 * GET /v6/seniors/:id/report
 */
export const getSeniorReport = async (seniorUuid) => {
  const response = await v5Api.get(
    `/v6/seniors/${encodeURIComponent(seniorUuid)}/report`,
  );
  return response.data;
};

/**
 * Persist or clear one clinician override for a FIM item on a specific report.
 * The report id must come from the consolidated GET currently being rendered.
 * PATCH /v6/seniors/:seniorId/report/:reportId
 */
export const updateSeniorReportFimOverride = async (
  seniorUuid,
  reportId,
  itemId,
  fimOverride,
) => {
  const response = await v5Api.patch(
    `/v6/seniors/${encodeURIComponent(seniorUuid)}/report/${encodeURIComponent(
      reportId,
    )}`,
    {
      fim_overrides: [
        {
          item_id: itemId,
          fim_override: fimOverride,
        },
      ],
    },
  );
  return response.data;
};

/**
 * Assets Management
 */

// Get Asset Token
export const getAssetToken = async (path, moduleId, seniorId) => {
  const response = await v5Api.get(
    `/v5/assets/token?path=${path}&moduleId=${moduleId}&seniorId=${seniorId}&storage=gcp`,
  );
  return response.data;
};

// Construct Video URL with SAS Token
export const getVideoUrl = async (path, moduleId, seniorId) => {
  try {
    const tokenResponse = await getAssetToken(path, moduleId, seniorId);
    // Adjust based on actual API response structure
    const token = tokenResponse?.items || tokenResponse?.token || tokenResponse;
    const accountName = ENV.AZURE_STORAGE_ACCOUNT_NAME;
    const containerName = ENV.AZURE_STORAGE_CONTAINER_NAME;

    if (!token) {
      console.warn("No token received for asset:", path);
      return null;
    }

    return `https://${accountName}.blob.core.windows.net/${containerName}/${path}?${token}`;
  } catch (error) {
    console.error("Error constructing video URL:", error);
    return null;
  }
};

/**
 * Mobility report sharing — recipients CRUD, PDF send, and remarks.
 * Mirrors senior-life-rom's romReports service (v5/report/* + reports PUT) so the
 * mobility report's Share modal and remark authoring hit the same backend.
 */

// List the account's saved report recipients.
export const getReportRecipients = async () => {
  const response = await v5Api.get(`/v5/report/recipient`);
  return response.data;
};

// Add a new recipient. A 409 means the email already exists.
export const createReportRecipient = async ({ firstname, lastname, email }) => {
  const response = await v5Api.post(`/v5/report/recipient`, {
    firstname,
    lastname,
    email,
  });
  return response.data;
};

export const updateReportRecipient = async ({
  id,
  firstname,
  lastname,
  email,
}) => {
  const response = await v5Api.patch(`/v5/report/recipient/${id}`, {
    firstname,
    lastname,
    email,
  });
  return response.data;
};

export const deleteReportRecipient = async (id) => {
  const response = await v5Api.delete(`/v5/report/recipient/${id}`);
  return response.data;
};

// Email the report to the selected recipients.
// request: { action, printURL, module, recepients: [{ recepientType, value }] }
export const sendReportPdf = async (seniorUuid, request) => {
  const response = await v5Api.post(`/v5/report/send/${seniorUuid}`, request);
  return response.data;
};

// Save a staff observation / test-person remark against a report.
// request: { staffObservation, moduleId } or { patientRemarks, moduleId }
export const addReportComments = async (seniorUuid, reportId, request) => {
  const response = await v5Api.put(
    `/seniors/${seniorUuid}/reports/${reportId}`,
    request,
  );
  return response.data;
};

export default v5Api;
